import AgencyHelper, { AgencyConstants } from 'features/Agency/agencyHelper';
import KTFormInput, {
  KTFormInputType,
} from 'general/components/OtherKeenComponents/Forms/KTFormInput';
import KTRadio, {
  KTRadioType,
} from 'general/components/OtherKeenComponents/KTFormControls/KTRadio';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import ImageView from 'general/components/quybka/Images/ImageView';
import AppColors from 'general/constants/AppColors';
import miscHelper from 'general/helpers/miscHelper';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';

AgencySaleCreateContractView.propTypes = {
  title: PropTypes.string,
  contentEl: PropTypes.element,
  onCancel: PropTypes.func,
  onConfirm: PropTypes.func,
  saleDetail: PropTypes.object,
  defaultContractType: PropTypes.string,
};

function AgencySaleCreateContractView({
  title = '',
  contentEl = null,
  onCancel,
  onConfirm,
  saleDetail,
  defaultContractType = '',
}) {
  // MARK: ---- PROPERTIES ----
  const [contractDate, setContractDate] = useState(moment().format('YYYY-MM-DD'));
  const [contractNumber, setContractNumber] = useState('');
  const [validFrom, setValidFrom] = useState(moment().format('YYYY-MM-DD'));
  const [validTo, setValidTo] = useState(moment().add(10, 'year').format('YYYY-MM-DD'));
  const [selectedFiles, setSelectedFiles] = useState([]);
  const selectFilePathList = useRef([]);
  const [allFiles, setAllFiles] = useState([]);
  const [contractType, setContractType] = useState(AgencyConstants.ContractType.DIGITAL);
  const [paperContractDocumentFile, setPaperContractDocumentFile] = useState(null);
  const [paperPOADocumentFile, setPaperPOADocumentFile] = useState(null);
  const [shippingAddress, setShippingAddress] = useState('');
  useEffect(() => {
    if (defaultContractType?.length) {
      setContractType(defaultContractType);
    }
  }, [defaultContractType])

  useEffect(() => {
    const getContractNumber = async (orderType) => {
      const contractNumber = await AgencyHelper.API.requestGenerateContractNumber({ orderType });
      if (contractNumber) {
        setContractNumber(contractNumber);
      }
    };
    if (saleDetail) {
      getContractNumber(saleDetail?.orderType);
    }
  }, [saleDetail]);

  const showCallOut = useMemo(() => {
    return saleDetail?.orderType === AgencyConstants.SaleOrderType.LANDLORD &&
      saleDetail?.contracts?.some(contract => contract?.status === AgencyConstants.ContractStatus.CANCELLED);
  }, [saleDetail]);

  // MARK: ---- FUNCTIONS ----
  const handleFilesChanged = (e) => {
    const newFiles = Array.from(e.target.files || []);

    const validFiles = newFiles.filter((file) => {
      if (file.name.length > 128) {
        ToastHelper.showError(`Tên file quá dài! Tối đa cho phép 128 ký tự.`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    setSelectedFiles((prev) => [...prev, ...validFiles]);
    setAllFiles((prev) => [...prev, ...validFiles]);
  };
  const handleRemoveAll = () => {
    setSelectedFiles([]);
  };
  const handleViewFile = (file) => {
    const url = URL.createObjectURL(file);
    if (url) {
      Utils.openInNewTab(url);
      URL.revokeObjectURL(url);
    }
  };
  const handleRemoveFile = (file) => {
    if (file instanceof File) {
      const tmpFiles = selectedFiles.filter((item) => item.lastModified !== file?.lastModified);
      setSelectedFiles(tmpFiles);
    }

    let newAllFiles = [...allFiles];
    if (typeof file === 'string') {
      newAllFiles = _.filter(allFiles, (item) => item !== file);
    }
    if (file instanceof File) {
      newAllFiles = _.filter(allFiles, (item) => item.lastModified !== file?.lastModified);
    }
    setAllFiles(newAllFiles);
  };
  async function processSelectedFiles(files) {
    for (const file of files) {
      const path = await uploadAsyncFunction(file);
      selectFilePathList.current?.push(path);
    }
  }
  async function uploadAsyncFunction(file) {
    return new Promise((resolve) => {
      if (file instanceof File) {
        miscHelper.callApiUploadFile(file).then((mediaList) => {
          if (_.isArray(mediaList) && mediaList.length > 0) {
            const firstMedia = _.first(mediaList);
            const relativePath = firstMedia?.relativePath;
            if (relativePath) {
              resolve(relativePath);
            } else {
              resolve(null);
            }
          } else {
            resolve(null);
          }
        });
      } else {
        resolve(file);
      }
    });
  }


  const getContractName = (contractNumber) => {
    const parts = (contractNumber || '').trim().split('/');
    const code = parts[2] || 'Không xác định';
    return `${code}_${parts[0] || ''}_${parts[1] || ''}_${code}.pdf`;
  };

  const handleUploadPaperContract = async (file, contractNumber) => {
    if (!file || !contractNumber) return;
    if (!(file instanceof File)) {
      return file;
    }

    const newFileName = getContractName(contractNumber);

    const renamedFile = new File([file], newFileName, {
      type: file.type,
      lastModified: file.lastModified,
    });

    try {
      const mediaList = await AgencyHelper.API.requestUploadPaperContract(renamedFile);

      if (Array.isArray(mediaList) && mediaList.length > 0) {
        return mediaList[0]?.relativePath || null;
      }
      return null;
    } catch (error) {
      console.error('Upload thất bại:', error);
      return null;
    }
  }

  const handleConfirm = async () => {
    if (!contractDate || !contractNumber) {
      ToastHelper.showError('Vui lòng nhập đầy đủ thông tin');
      return;
    }

    if (saleDetail?.orderType === AgencyConstants.SaleOrderType.GROUP ||
      saleDetail?.orderType === AgencyConstants.SaleOrderType.LANDLORD ||
      saleDetail?.orderType === AgencyConstants.SaleOrderType.INVESTOR
    ) {
      if (!validFrom || !validTo) {
        ToastHelper.showError('Vui lòng nhập đầy đủ thông tin');
        return;
      }
    }

    if (
      contractType === AgencyConstants.ContractType.DIGITAL &&
      saleDetail?.orderType === AgencyConstants.SaleOrderType.INVESTOR &&
      !shippingAddress
    ) {
      ToastHelper.showError('Vui lòng nhập đầy đủ thông tin');
      return;
    }

    if (
      contractType === AgencyConstants.ContractType.PAPER &&
      saleDetail?.orderType === AgencyConstants.SaleOrderType.GROUP &&
      (!paperContractDocumentFile || !paperPOADocumentFile)
    ) {
      ToastHelper.showError('Vui lòng tải lên hợp đồng scan và giấy uỷ quyền');
      return;
    }

    if (
      contractType === AgencyConstants.ContractType.PAPER &&
      (saleDetail?.orderType === AgencyConstants.SaleOrderType.INVESTOR ||
        saleDetail?.orderType === AgencyConstants.SaleOrderType.LANDLORD ||
        saleDetail?.orderType === AgencyConstants.SaleOrderType.CHARGEPOINT) &&
      !paperContractDocumentFile
    ) {
      ToastHelper.showError('Vui lòng tải lên hợp đồng scan');
      return;
    }

    if (!_.isEmpty(selectedFiles)) {
      selectFilePathList.current = [];
      await processSelectedFiles(selectedFiles);
    }
    setAllFiles([
      ..._.filter(allFiles, (item) => typeof item === 'string'),
      ...selectFilePathList.current,
    ]);
    const attachedFiles = [
      ..._.filter(allFiles, (item) => typeof item === 'string'),
      ...selectFilePathList.current,
    ];

    const param = {
      contractType: contractType,
      contractDate,
      contractNumber,
      validFrom,
      validTo,
      attachedFiles,
      contractDocument: '',
      poaDocument: '',
      shippingAddress,
    };

    if (contractType === AgencyConstants.ContractType.PAPER) {
      if (paperContractDocumentFile) {
        param.contractDocument = await handleUploadPaperContract(paperContractDocumentFile, contractNumber);
      }
      if (paperPOADocumentFile) {
        param.poaDocument = await uploadAsyncFunction(paperPOADocumentFile);
      }
      // gan luon trang thai la hoan thanh
      param.status = AgencyConstants.ContractStatus.COMPLETED;
    }

    onConfirm(param);
  };

  return (
    <div className="">
      <div className="d-flex flex-column align-items-center justify-content-center text-center p-5">
        <span style={UIHelper.buildFontStyle('600', '16px', AppColors.gray[800])}>{title}</span>
        <span style={UIHelper.buildFontStyle('400', '14px', AppColors.gray[600])}>{contentEl}</span>
      </div>

      {showCallOut && (
        <div className='px-4 pb-4'>
          <div className='p-4'
            style={{
              border: `1px solid ${AppColors.danger}`,
              borderRadius: 10,
              backgroundColor: '#FFF6F6',
            }}>
            <div style={UIHelper.buildFontStyle('700', '14px', AppColors.danger)}>
              Lưu ý:
            </div>
            <div style={UIHelper.buildFontStyle('400', '14px', AppColors.gray[800])}>
              Vui lòng kiểm tra, thiết lập lại hoa hồng và phí thuê mặt bằng ứng với mặt bằng
              <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                {' '}{saleDetail?.premise?.premiseCode || saleDetail?.premise?.premiseId} {' '}
              </span>
              trước khi tạo hợp đồng HTMB mới.
            </div>
          </div>
        </div>
      )}

      {/* chon loai hop dong */}
      <div
        style={{
          borderBottom: `1px dashed ${AppColors.gray[200]}`,
        }}
      >
        {!defaultContractType?.length && (
          <div className="radio-inline w-100 px-5 mb-3">
            <div className="d-flex align-items-center gap-5">
              <KTRadio
                name="typeContractElectronic"
                label="Hợp đồng điện tử"
                type={KTRadioType.outline}
                additionalClassName="radio-success"
                onChange={(value) => {
                  setContractType(AgencyConstants.ContractType.DIGITAL);
                }}
                checked={contractType === AgencyConstants.ContractType.DIGITAL}
              />
              <KTRadio
                name="typeContractPaper"
                label="Hợp đồng giấy"
                type={KTRadioType.outline}
                additionalClassName="radio-success"
                onChange={(value) => {
                  setContractType(AgencyConstants.ContractType.PAPER);
                }}
                checked={contractType === AgencyConstants.ContractType.PAPER}
              />
            </div>
          </div>
        )}
      </div>

      <div className="pb-5 d-flex flex-column gap-3 mt-4">
        {/* ngay hop dong */}
        <div className="row m-0 p-0 px-5">
          <div className={(saleDetail?.orderType === AgencyConstants.SaleOrderType.INVESTOR || saleDetail?.orderType === AgencyConstants.SaleOrderType.CHARGEPOINT)
            ? 'col-6 pl-0 pr-2' : 'col-12 px-0'}>
            {UIHelper.buildFormLabel('Ngày hợp đồng', true)}
            <KTFormInput
              name="contractDate"
              placeholder="Nhập ngày hợp đồng"
              type={KTFormInputType.date}
              value={contractDate}
              onChange={(value) => setContractDate(value)}
            />
          </div>
          {(saleDetail?.orderType === AgencyConstants.SaleOrderType.INVESTOR || saleDetail?.orderType === AgencyConstants.SaleOrderType.CHARGEPOINT) && (
            <div className="col-6 pr-0 pl-2">
              {UIHelper.buildFormLabel('Ngày hiệu lực', true)}
              <KTFormInput
                name="contractValidFrom"
                placeholder="Nhập ngày hiệu lực"
                type={KTFormInputType.date}
                value={validFrom}
                onChange={(value) => setValidFrom(value)}
                minTime={contractDate}
              />
            </div>
          )}
        </div>
        {/* so hop dong */}
        <div className="px-5">
          {UIHelper.buildFormLabel('Số hợp đồng', true)}
          <KTFormInput
            name="contractNumber"
            placeholder="Nhập số hợp đồng"
            type={KTFormInputType.text}
            value={contractNumber}
            onChange={(value) => setContractNumber(value)}
          />
        </div>
        {/* dia diem ban giao san pham */}
        {(saleDetail?.orderType === AgencyConstants.SaleOrderType.INVESTOR && contractType === AgencyConstants.ContractType.DIGITAL) && (
          <div className="px-5">
            {UIHelper.buildFormLabel('Địa điểm bàn giao sản phẩm', true)}
            <KTFormInput
              name="shippingAddress"
              placeholder="Nhập địa điểm bàn giao sản phẩm"
              type={KTFormInputType.text}
              value={shippingAddress}
              onChange={(value) => setShippingAddress(value)}
            />
          </div>
        )}
        {/* ngay hieu luc */}
        {(saleDetail?.orderType === AgencyConstants.SaleOrderType.GROUP ||
          saleDetail?.orderType === AgencyConstants.SaleOrderType.LANDLORD) && (
            <div className="row m-0 p-0 px-5">
              <div className="col-6 pl-0 pr-2">
                {UIHelper.buildFormLabel('Ngày hiệu lực', true)}
                <KTFormInput
                  name="contractValidFrom"
                  placeholder="Nhập ngày hiệu lực"
                  type={KTFormInputType.date}
                  value={validFrom}
                  onChange={(value) => setValidFrom(value)}
                  minTime={contractDate}
                />
              </div>
              <div className="col-6 pr-0 pl-2">
                {UIHelper.buildFormLabel('Ngày hết hiệu lực', true)}
                <KTFormInput
                  name="contractValidTo"
                  placeholder="Nhập ngày hết hiệu lực"
                  type={KTFormInputType.date}
                  value={validTo}
                  onChange={(value) => setValidTo(value)}
                  minTime={validFrom}
                />
              </div>
            </div>
          )}
        {/* file dinh kem */}
        {contractType === AgencyConstants.ContractType.DIGITAL && (
          <div className="">
            <div className="px-5 d-flex align-items-center gap-1">
              {contractType === AgencyConstants.ContractType.DIGITAL
                ? UIHelper.buildFormLabel('Tải lên tệp tin đính kèm', false, false)
                : UIHelper.buildFormLabel('Tải lên hợp đồng scan và giấy uỷ quyền', true)}
              {contractType === AgencyConstants.ContractType.DIGITAL && (
                <KTTooltip text="Tệp đính kèm tải lên sẽ không ảnh hưởng đến nội dung hợp đồng điện tử.">
                  <i className="fa-regular fa-circle-info" style={{ color: AppColors.info }} />
                </KTTooltip>
              )}
            </div>
            <div>
              {/* danh sach file dang duoc chon */}
              {!_.isEmpty(allFiles) && (
                <div className="row m-0 p-0 px-3">
                  {allFiles.map((item, index) => {
                    return (
                      <div className="p-0 col-6 mb-2 p-2" key={item?.lastModified ?? item ?? index}>
                        <ImageView
                          name={item?.lastModified ?? item ?? index}
                          path={_.isString(item) ? item : item?.name}
                          onRemove={() => {
                            handleRemoveFile(item);
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="d-flex px-5">
                <label className="">
                  {UIHelper.buildNegativeButton(
                    'Thêm tập tin',
                    () => { },
                    <i className="fa-regular fa-upload" />
                  )}
                  <input
                    type="file"
                    accept={['.xls', '.xlsx', '.doc', '.docx', '.pdf']}
                    onChange={handleFilesChanged}
                    multiple
                    hidden
                    onClick={(e) => {
                      e.target.value = null;
                    }}
                  />
                </label>
              </div>
            </div>
          </div>
        )}

        {/* file hop dong & file giay uy quyen truong hop hop dong giay (PAPER) */}
        {contractType === AgencyConstants.ContractType.PAPER && (
          <div>
            <div className="row m-0 p-0 px-3">
              {/* file hop dong */}
              <div className="col-6 p-0 px-2">
                <div>
                  {UIHelper.buildFormLabel('Tải lên hợp đồng scan', true)}
                  <label className="">
                    {UIHelper.buildNegativeButton(
                      'Thêm tập tin',
                      () => { },
                      <i className="fa-regular fa-upload" />
                    )}
                    <input
                      type="file"
                      accept={['.xls', '.xlsx', '.doc', '.docx', '.pdf']}
                      onChange={(e) => {
                        const files = e.target.files;
                        if (!files || files.length === 0) return;
                        const file = files[0];
                        if (file.name.length > 128) {
                          ToastHelper.showError("Tên file quá dài! Tối đa cho phép 128 ký tự.");
                          e.target.value = null;
                          return;
                        }
                        setPaperContractDocumentFile(file);
                      }}
                      hidden
                      onClick={(e) => {
                        e.target.value = null;
                      }}
                    />
                  </label>
                  {paperContractDocumentFile && (
                    <div>
                      <span
                        className="text-ellipsis-2-lines"
                        style={UIHelper.buildFontStyle('500', '12px', AppColors.gray[700])}
                      >
                        {paperContractDocumentFile.name}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              {/* giay uy quyen */}
              {saleDetail?.orderType === AgencyConstants.SaleOrderType.GROUP && (
                <div className="col-6 p-0 px-2">
                  <div>
                    {UIHelper.buildFormLabel('Tải lên giấy uỷ quyền', true)}
                    <label className="">
                      {UIHelper.buildNegativeButton(
                        'Thêm tập tin',
                        () => { },
                        <i className="fa-regular fa-upload" />
                      )}
                      <input
                        type="file"
                        accept={['.xls', '.xlsx', '.doc', '.docx', '.pdf']}
                        onChange={(e) => {
                          const files = e.target.files;
                          if (!files || files.length === 0) return;
                          const file = files[0];
                          if (file.name.length > 128) {
                            ToastHelper.showError("Tên file quá dài! Tối đa cho phép 128 ký tự.");
                            e.target.value = null;
                            return;
                          }
                          setPaperPOADocumentFile(file);
                        }}
                        hidden
                        onClick={(e) => {
                          e.target.value = null;
                        }}
                      />
                    </label>
                    {paperPOADocumentFile && (
                      <div>
                        <span
                          className="text-ellipsis-2-lines"
                          style={UIHelper.buildFontStyle('500', '12px', AppColors.gray[700])}
                        >
                          {paperPOADocumentFile.name}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* footer */}
      <div
        className="d-flex align-items-center gap-3 px-4 py-3"
        style={{
          borderTop: `1px solid ${AppColors.gray[200]}`,
        }}
      >
        {UIHelper.buildNegativeButton('Hủy bỏ', onCancel, null, true)}
        {UIHelper.buildGreenButton('Xác nhận', handleConfirm, null, true)}
      </div>
    </div>
  );
}

export default AgencySaleCreateContractView;
